// Task groups, tasks, their steps and timeline as the admin pages see them (docs/12 "Task groups
// as built", "Execution as built"; docs/03 TaskGroup, Task, TaskStep). The generated schema
// types the JSON columns (`active_step`, `visits`, `outputs`, the graph, a timeline entry's
// `detail`) as unknown; parse.ts narrows them into these.
import type { Orchestration, WaitingOn } from "./subtasks/api";
import type { Schemas } from "../api/client";
import type { Graph, Requirement } from "../catalog/types";

export const GROUP_STATUSES = ["active", "paused", "archived"] as const;
export type GroupStatus = (typeof GROUP_STATUSES)[number];

export const SOURCES = ["manual", "github", "jira", "asana"] as const;
export type Source = (typeof SOURCES)[number];

export const TASK_STATUSES = ["queued", "running", "waiting_for_human", "succeeded", "failed", "cancelled"] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export type Isolation = "container" | "vm";

type GroupDto = Schemas["TaskGroupDto"];

/** A GitHub repository the group's workspace clones besides a GitHub source's own (S56). */
export interface GroupRepository {
  /** `owner/name`. */
  repo: string;
  /** `main` when unset. */
  default_branch: string | null;
}

export interface TaskGroup extends Omit<GroupDto, "status" | "source" | "isolation" | "source_config" | "bindings" | "repositories"> {
  status: GroupStatus;
  source: Source;
  isolation: Isolation;
  source_config: Record<string, unknown>;
  repositories: GroupRepository[];
  /** Requirement index → credential id or `"platform"`. */
  bindings: Record<string, string>;
  /** D81: the network list the agents' containers use (null: the platform defaults). */
  network_list_id: string | null;
  share_vm_network: boolean;
}

/** The step in flight (docs/03 `task.active_step`). */
export type ActiveStep =
  | { kind: "prompt"; node: string; visit: number; run: string }
  | { kind: "approval"; node: string; visit: number; question: string }
  /** A parent running its subtasks (D87): no run and no question behind it. */
  | { kind: "orchestrate"; node: string; visit: number };

type TaskDto = Schemas["TaskDto"];

export interface Task
  extends Omit<TaskDto, "status" | "visits" | "active_step" | "parent_task_id" | "child_count" | "waiting_on" | keyof Orchestration>,
    Orchestration {
  status: TaskStatus;
  /** What the task waits on (S141), including its subtasks or its parent (D87). */
  waiting_on: WaitingOn | null;
  visits: Record<string, number>;
  active_step: ActiveStep | null;
  /** The task this one belongs under (D66), of the same group. */
  parent_task_id: string | null;
  child_count: number;
}

export type StepKind = "prompt" | "approval" | "orchestrate" | "end";

export interface TaskStep extends Omit<Schemas["TaskStepDto"], "kind" | "outputs" | "error"> {
  kind: StepKind;
  outputs: Record<string, string>;
  /** Why the step did not succeed, or a pre-execution script that failed open (S56). */
  error?: string | null;
}

export interface TaskDetail {
  task: Task;
  steps: TaskStep[];
  workflow_version_id: string;
  definition: Graph;
  current_node: string | null;
}

interface StepRef {
  at: number;
  node: string;
  nodeName: string;
  visit: number;
}

/** One timeline entry, by kind, with its `detail` typed. */
export type TimelineEntry =
  | { kind: "status"; at: number; status: TaskStatus; reason: string | null }
  | (StepRef & { kind: "step_started"; stepKind: StepKind; runId: string | null })
  | (StepRef & {
      kind: "step_finished";
      stepKind: StepKind;
      runId: string | null;
      outcome: string | null;
      outputs: Record<string, string>;
      error: string | null;
      costUsd: number;
      inputTokens: number;
      outputTokens: number;
    })
  | (Omit<StepRef, "visit"> & { kind: "approval_asked"; question: string; options: string[] })
  | { kind: "approval_answered"; at: number; node: string; answer: string };

/** Requirement readiness (docs/12 "Task groups as built"). */
export const REQUIREMENT_STATES = ["bound", "platform", "unbound", "missing", "broken", "not_enforced", "recorded"] as const;
export type RequirementState = (typeof REQUIREMENT_STATES)[number];

export interface RequirementStatus {
  index: number;
  requirement: Requirement;
  state: RequirementState;
  satisfied: boolean;
  /** A credential id, `"platform"`, or null. */
  binding: string | null;
}

export interface GroupRequirements {
  workflow_version_id: string;
  satisfied: boolean;
  requirements: RequirementStatus[];
}

export const FINISHED: ReadonlySet<TaskStatus> = new Set(["succeeded", "failed", "cancelled"]);
